// Which of your rows have a sale that is accepted but not yet final.
//
// WHAT "SETTLING" MEANS
// A CSFloat sale goes pending -> verified, with Steam's trade-protection window
// in between. The money is agreed but reversible, so services/saleReconciler.js
// deliberately writes nothing onto the row: marking it sold would book revenue
// that can still be rolled back. It reports these separately instead, as
// `inFlight`.
//
// The cost of that correctness is that the row looks untouched everywhere in the
// app — an item you have effectively sold reads as one you still hold. The
// banner said so in a sentence; nothing else knew.
//
// WHY A CONTEXT AND NOT A PROP
// The one place this was asked for is the trade chain in ItemDetailModal, and
// that modal is rendered from fifteen tabs. Passing `inFlight` down all of them
// would be fifteen edits to say one thing, and every future consumer would be a
// sixteenth. The state is ephemeral by nature — it is a report about trades in
// flight, not a fact about the row — so it lives in client state and is read
// where it is needed.
//
// Nothing here writes. A component that reads no provider gets `null` for every
// row, which is exactly what it showed before this existed.

import React, { createContext, useContext, useMemo } from 'react';

const SettlingContext = createContext(null);

const CONDITION_IN_NAME =
  /\s*\((Factory New|Minimal Wear|Field[- ]Tested|Well[- ]Worn|Battle[- ]Scarred)\)\s*$/;

const stripCondition = name => String(name || '').replace(CONDITION_IN_NAME, '').trim();

/**
 * float + paint seed on a named skin — one item in the world.
 *
 * Nine decimals, matching services/saleReconciler.js, so a row the server
 * identified hashes the same here. Name and condition alone are deliberately
 * NOT a key: three identical Redlines are three different items, and marking
 * the wrong one as sold-and-settling is the same class of mistake this whole
 * area exists to avoid.
 */
function fingerprint(name, float, seed) {
  if (float == null || seed == null) return null;
  const base = stripCondition(name);
  if (!base) return null;
  return `fp:${base}|${Number(float).toFixed(9)}|${Number(seed)}`;
}

/**
 * name + pattern, for things that HAVE a pattern but no float.
 *
 * A charm is the case this exists for. Its pattern runs 1–100000 and is minted
 * with the charm, so "Charm | Pocket AWP, pattern 93753" names one charm out of
 * twenty identical-looking ones exactly as well as a float does for a skin.
 *
 * Only generated when the float is ABSENT. An item that has a float is the
 * business of the fingerprint above and must stay there: matching a skin on
 * name + pattern alone would be a weaker rule than the one it already passes,
 * and the point of this tier is to cover what that rule cannot reach, not to
 * loosen it.
 */
function patternKey(name, float, seed) {
  if (float != null || seed == null) return null;
  const base = stripCondition(name);
  if (!base) return null;
  return `pt:${base}|${Number(seed)}`;
}

/**
 * The pattern that identifies this copy.
 *
 * `paintSeed` for everything, charms included: a charm carries exactly one
 * number and it lands in that field wherever the item is read — Steam's asset
 * property 3, or CSFloat's keychain_pattern.
 *
 * `charms[0].pattern` is only a fallback for an item read through the CSFloat
 * relay, which reports a standalone charm's pattern in its charms list rather
 * than on the item. It is safe here precisely because this function is only ever
 * consulted for items with NO float: a weapon wearing a charm has one, so its
 * charm's pattern can never be mistaken for the weapon's identity.
 */
function patternOf(item) {
  if (item?.paintSeed != null) return item.paintSeed;
  const own = Array.isArray(item?.charms) ? item.charms.find(c => c?.pattern != null) : null;
  return own ? own.pattern : null;
}

/**
 * Every key one in-flight sale, or one row, answers to — strongest first.
 *
 *   asset:  the Steam id, when both sides happen to know it
 *   fp:     float + pattern — one skin in the world
 *   pt:     name + pattern, for patterned things with no float (charms)
 *
 * An item with none of these — a case, a sticker, a patch, a capsule — cannot
 * be identified at all, and is handled separately in matchSettling.
 */
function keysOf(item) {
  const { name, float, assetId } = item;
  const keys = [];
  if (assetId) keys.push(`asset:${assetId}`);
  const fp = fingerprint(name, float, item.paintSeed);
  if (fp) keys.push(fp);
  const pt = patternKey(name, float, patternOf(item));
  if (pt) keys.push(pt);
  return keys;
}

/**
 * Does the item itself carry something that tells one copy from another?
 *
 * float + pattern, or pattern alone. Deliberately NOT the assetId: Steam
 * re-issues one every time an item moves, so a row and the sale of that very
 * item routinely hold different ids, and a row created from a trade holds none
 * at all. An id is worth trying as a key — it is in keysOf — but it is worthless
 * as a test of "could this ever have been told apart", which is what decides
 * whether an item is allowed into the counted name pool below.
 */
function hasCopyIdentity(item) {
  return !!(fingerprint(item?.name, item?.float, item?.paintSeed)
    || patternKey(item?.name, item?.float, patternOf(item)));
}

/** name + wear: a KIND of item, not one item. The last-resort key. */
function nameKey(name, condition) {
  const base = stripCondition(name).toLowerCase();
  if (!base) return null;
  const cond = String(condition ?? '').toLowerCase().replace(/[^a-z]/g, '');
  return `nm:${base}|${cond}`;
}

/** A row that still looks open: not sold by hand, not traded away. */
function isOpenRow(row) {
  return !!row?.skin?.value && !(parseFloat(row.sellPrice) > 0) && !row.tradeId;
}

/**
 * Match the in-flight sales to the rows they belong to, once for the row list.
 *
 * TWO PASSES, AND THE SECOND ONE IS WHY THIS IS NOT A SIMPLE LOOKUP
 *
 * Strong first, and that rule is UNCHANGED from the version that only had one
 * pass: assetId, or float + paint seed for skins, plus name + pattern for
 * patterned things with no float. Every one of those names a specific copy, so
 * a match is proof and is reported as `certain`.
 *
 * What it cannot reach is anything with no float, no pattern and no id: cases,
 * stickers, patches, capsules. Nothing about a sticker distinguishes it from
 * the identical sticker beside it — not in Steam's data and not in CSFloat's —
 * and a row created from a Steam trade has no assetId either, since trade
 * history never reveals the id an item lands under.
 *
 * So the second pass takes ONLY those, and matches on name and wear, BOUNDED by
 * how many actually sold: if one of your five identical patch packs sold,
 * exactly one row is marked, never five. Which of five identical copies is
 * arbitrary — they are fungible, and the count is what has to be right. Weak
 * matches carry `certain: false` so a caller can say so.
 *
 * A patterned item NEVER falls through to that pass. Twenty Pocket AWP charms
 * are twenty different charms, and if pattern 93753 is the one that sold, the
 * other nineteen are not on their way anywhere. When the pattern is known it
 * decides; when it is not recorded on both sides, the charm falls to the name
 * pool with everything else, which is the same answer as "we cannot tell them
 * apart" — because in that case nothing in the data can.
 *
 * Nothing here writes, and no money is decided by it: the reconciler still
 * refuses to book a sale it cannot prove. This only decides which card wears a
 * "settling" badge.
 */
export function matchSettling(inFlight, rows) {
  const byRow = new Map();
  const sales = (inFlight || []).filter(Boolean);
  if (!sales.length || !rows?.length) return byRow;

  const claimed = new Set();

  // ── Pass 1: proof ────────────────────────────────────────
  const strong = new Map();
  for (const sale of sales) {
    for (const key of keysOf(sale)) if (!strong.has(key)) strong.set(key, sale);
  }

  const leftovers = [];
  for (const row of rows) {
    if (!isOpenRow(row)) continue;
    const keys = keysOf({
      name: row.skin?.value, float: row.float, paintSeed: row.paintSeed,
      charms: row.charms, assetId: row.assetId,
    });
    let hit = null;
    for (const key of keys) {
      const found = strong.get(key);
      if (found && !claimed.has(found)) { hit = found; break; }
    }
    if (hit) {
      byRow.set(row, { ...hit, certain: true });
      claimed.add(hit);
    } else if (!hasCopyIdentity({ name: row.skin?.value, float: row.float, paintSeed: row.paintSeed, charms: row.charms })) {
      // Nothing about this row identifies the copy — a case, a sticker, a
      // patch, a capsule — which is exactly what pass 2 is for. A row that HAS
      // a key and did not match is a different copy: a skin with another float,
      // or a charm with another pattern. Those are left alone.
      leftovers.push(row);
    }
  }

  // ── Pass 2: name and wear, capped by how many actually sold ──
  const unclaimed = sales.filter(s => !claimed.has(s));
  if (!unclaimed.length || !leftovers.length) return byRow;

  const pool = new Map();
  for (const sale of unclaimed) {
    // A sale that names a specific copy has had its chance above. Letting it
    // into the pool would let a charm whose pattern did not match be pinned on
    // some other charm of the same name — the one thing the pattern rule above
    // exists to prevent.
    if (hasCopyIdentity(sale)) continue;
    const key = nameKey(sale.name, sale.condition);
    if (!key) continue;
    if (!pool.has(key)) pool.set(key, []);
    pool.get(key).push(sale);
  }

  for (const row of leftovers) {
    const key = nameKey(row.skin?.value, row.condition?.value);
    const queue = key && pool.get(key);
    if (!queue?.length) continue;
    byRow.set(row, { ...queue.shift(), certain: false });
  }

  return byRow;
}

export function SettlingProvider({ inFlight, rows, children }) {
  const value = useMemo(() => matchSettling(inFlight, rows), [inFlight, rows]);
  return <SettlingContext.Provider value={value}>{children}</SettlingContext.Provider>;
}

/**
 * The in-flight sale for a row, or null.
 *
 * Keyed on the row OBJECT, the way the rest of the app identifies rows — an
 * index would go stale the moment a reconcile or an autosave rebuilds the
 * array. Answers null for everything when no provider is mounted: the mobile
 * shell, or a component rendered on its own.
 */
export function useSettling() {
  const byRow = useContext(SettlingContext);
  return useMemo(() => {
    if (!byRow?.size) return () => null;
    return (row) => (row ? byRow.get(row) || null : null);
  }, [byRow]);
}

/** "settles in 3 days" / "settles today", or null when the date is unknown. */
export function settlesInLabel(settlesAt) {
  const at = Date.parse(settlesAt || '');
  if (!Number.isFinite(at)) return null;
  const days = Math.ceil((at - Date.now()) / 86_400_000);
  if (days <= 0) return 'settling now';
  return `settles in ${days} day${days === 1 ? '' : 's'}`;
}
